/**
 * SearchField
 *
 * The one search box for list pages. Pages used to build their own: some searched on every
 * keystroke (a request per letter), some only on Enter (typing and waiting did nothing), and
 * several had a `minWidth: 300` that overflowed a phone.
 *
 * This one keeps the typed text locally and calls `onSearch` 350ms after typing stops, on Enter
 * immediately, and with '' when cleared. It is full width on a phone and `maxWidth` wide above.
 *
 *   <SearchField value={search} onSearch={(q) => { setSearch(q); setPage(0) }} placeholder="Search users" />
 */

import { useEffect, useRef, useState } from 'react'
import { IconButton, InputAdornment, TextField } from '@mui/material'
import { Clear, Search } from '@mui/icons-material'
import type { SxProps, Theme } from '@mui/material/styles'

export interface SearchFieldProps {
  /** The committed search (what the list is currently filtered by). */
  value: string
  onSearch: (value: string) => void
  placeholder?: string
  /** Width cap at sm and up. Default 360. */
  maxWidth?: number | string
  delayMs?: number
  autoFocus?: boolean
  sx?: SxProps<Theme>
}

export function SearchField({
  value,
  onSearch,
  placeholder = 'Search',
  maxWidth = 360,
  delayMs = 350,
  autoFocus,
  sx,
}: SearchFieldProps) {
  const [text, setText] = useState(value)
  const lastSent = useRef(value)

  // The parent can reset the search (a "clear filters" button, a tab change); follow it.
  useEffect(() => {
    if (value !== lastSent.current) {
      lastSent.current = value
      setText(value)
    }
  }, [value])

  const send = (next: string) => {
    const trimmed = next.trim()
    if (trimmed === lastSent.current) return
    lastSent.current = trimmed
    onSearch(trimmed)
  }

  useEffect(() => {
    const id = setTimeout(() => send(text), delayMs)
    return () => clearTimeout(id)
    // `send` reads only refs and the latest onSearch; re-arming on text alone is the point.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, delayMs])

  return (
    <TextField
      size="small"
      type="search"
      value={text}
      autoFocus={autoFocus}
      placeholder={placeholder}
      onChange={(e) => setText(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') send(text)
      }}
      inputProps={{ 'aria-label': placeholder, enterKeyHint: 'search' }}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Search fontSize="small" />
          </InputAdornment>
        ),
        endAdornment: text ? (
          <InputAdornment position="end">
            <IconButton
              aria-label="Clear search"
              edge="end"
              size="small"
              onClick={() => {
                setText('')
                send('')
              }}
            >
              <Clear fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : undefined,
      }}
      sx={{
        width: { xs: '100%', sm: maxWidth },
        // The browser's own clear "x" on type=search duplicates ours.
        '& input::-webkit-search-cancel-button': { display: 'none' },
        ...sx,
      }}
    />
  )
}
